/** Every permission the platform recognises. Stored in `permissions` table (key = PK). */
export const PERMISSIONS = {
  ORG_SETTINGS_MANAGE: 'org.settings.manage',
  ORG_DELETE: 'org.delete',
  ORG_MEMBERS_VIEW: 'org.members.view',
  ORG_MEMBERS_INVITE: 'org.members.invite',
  ORG_MEMBERS_MANAGE: 'org.members.manage',
  ORG_MEMBERS_REMOVE: 'org.members.remove',
  ORG_ROLES_MANAGE: 'org.roles.manage',
  ORG_OWNERSHIP_TRANSFER: 'org.ownership.transfer',

  SITE_VIEW: 'site.view',
  SITE_CREATE: 'site.create',
  SITE_CONFIG_MANAGE: 'site.config.manage',
  SITE_DELETE: 'site.delete',
  SITE_DOMAINS_MANAGE: 'site.domains.manage',
  SITE_ENVIRONMENTS_MANAGE: 'site.environments.manage',

  CONTENT_VIEW: 'content.view',
  CONTENT_EDIT: 'content.edit',
  CONTENT_PUBLISH: 'content.publish',
  CONTENT_SCHEDULE: 'content.schedule',
  CONTENT_VERSIONS_VIEW: 'content.versions.view',
  CONTENT_VERSIONS_RESTORE: 'content.versions.restore',
  CONTENT_MODELS_MANAGE: 'content.models.manage',

  MEDIA_VIEW: 'media.view',
  MEDIA_MANAGE: 'media.manage',

  API_KEYS_MANAGE: 'api_keys.manage',
  WEBHOOKS_MANAGE: 'webhooks.manage',

  LICENCES_VIEW: 'licences.view',
  LICENCES_MANAGE: 'licences.manage',

  BILLING_VIEW: 'billing.view',
  BILLING_MANAGE: 'billing.manage',
  BILLING_INVOICES_DOWNLOAD: 'billing.invoices.download',

  AUDIT_VIEW: 'audit.view',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ALL_PERMISSIONS: Permission[] = Object.values(PERMISSIONS);

/** Grouping + human copy used by the admin/roles UI and the seed. */
export const PERMISSION_META: Record<Permission, { group: string; description: string }> = {
  [PERMISSIONS.ORG_SETTINGS_MANAGE]: { group: 'Organisation', description: 'Edit organisation settings' },
  [PERMISSIONS.ORG_DELETE]: { group: 'Organisation', description: 'Delete the organisation' },
  [PERMISSIONS.ORG_MEMBERS_VIEW]: { group: 'Team', description: 'View team members' },
  [PERMISSIONS.ORG_MEMBERS_INVITE]: { group: 'Team', description: 'Invite new members' },
  [PERMISSIONS.ORG_MEMBERS_MANAGE]: { group: 'Team', description: 'Change member roles and status' },
  [PERMISSIONS.ORG_MEMBERS_REMOVE]: { group: 'Team', description: 'Remove members' },
  [PERMISSIONS.ORG_ROLES_MANAGE]: { group: 'Team', description: 'Create and edit custom roles' },
  [PERMISSIONS.ORG_OWNERSHIP_TRANSFER]: { group: 'Organisation', description: 'Transfer ownership' },
  [PERMISSIONS.SITE_VIEW]: { group: 'Sites', description: 'View sites' },
  [PERMISSIONS.SITE_CREATE]: { group: 'Sites', description: 'Create sites' },
  [PERMISSIONS.SITE_CONFIG_MANAGE]: { group: 'Sites', description: 'Edit site configuration' },
  [PERMISSIONS.SITE_DELETE]: { group: 'Sites', description: 'Delete sites' },
  [PERMISSIONS.SITE_DOMAINS_MANAGE]: { group: 'Sites', description: 'Manage domains and verification' },
  [PERMISSIONS.SITE_ENVIRONMENTS_MANAGE]: { group: 'Sites', description: 'Manage environments' },
  [PERMISSIONS.CONTENT_VIEW]: { group: 'Content', description: 'View content' },
  [PERMISSIONS.CONTENT_EDIT]: { group: 'Content', description: 'Edit draft content' },
  [PERMISSIONS.CONTENT_PUBLISH]: { group: 'Content', description: 'Publish and unpublish content' },
  [PERMISSIONS.CONTENT_SCHEDULE]: { group: 'Content', description: 'Schedule publishing' },
  [PERMISSIONS.CONTENT_VERSIONS_VIEW]: { group: 'Content', description: 'View version history' },
  [PERMISSIONS.CONTENT_VERSIONS_RESTORE]: { group: 'Content', description: 'Restore previous versions' },
  [PERMISSIONS.CONTENT_MODELS_MANAGE]: { group: 'Content', description: 'Manage content models and fields' },
  [PERMISSIONS.MEDIA_VIEW]: { group: 'Media', description: 'View the media library' },
  [PERMISSIONS.MEDIA_MANAGE]: { group: 'Media', description: 'Upload, edit and delete media' },
  [PERMISSIONS.API_KEYS_MANAGE]: { group: 'Developer', description: 'Manage API keys' },
  [PERMISSIONS.WEBHOOKS_MANAGE]: { group: 'Developer', description: 'Manage webhooks and deliveries' },
  [PERMISSIONS.LICENCES_VIEW]: { group: 'Licensing', description: 'View licences and activations' },
  [PERMISSIONS.LICENCES_MANAGE]: { group: 'Licensing', description: 'Activate and deactivate sites' },
  [PERMISSIONS.BILLING_VIEW]: { group: 'Billing', description: 'View billing information' },
  [PERMISSIONS.BILLING_MANAGE]: { group: 'Billing', description: 'Change plans and payment methods' },
  [PERMISSIONS.BILLING_INVOICES_DOWNLOAD]: { group: 'Billing', description: 'Download invoices' },
  [PERMISSIONS.AUDIT_VIEW]: { group: 'Security', description: 'View audit logs' },
};
